'use client';

import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Suspense } from 'react';
import Loading from 'src/components/Loading/Loading';
import { DoctorFactsProps } from 'src/components/DoctorFacts/DoctorFacts';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { Content, FilledLinkToWebField } from '@prismicio/client';

const DoctorFacts = dynamic<DoctorFactsProps>(
    () => import('src/components/DoctorFacts/DoctorFacts'),
    { suspense: true },
);

type FactsProps = SliceComponentProps<Content.FactsSlice>;

const Facts = ({ slice }: FactsProps) => {
    // Doctor
    const doctorFactsCardProps = useMemo(
        () => ({
            image: {
                src: slice.primary?.doctor_image?.url || '',
                width: slice.primary?.doctor_image?.dimensions?.width || 0,
                height: slice.primary?.doctor_image?.dimensions?.height || 0,
            },
            name: slice.primary?.doctor_name || '',
            title: slice.primary?.doctor_title || '',
            title2: slice.primary?.doctor_title2 || '',
            description: `"${slice.primary?.doctor_description}"` || '',
        }),
        [slice],
    );

    // Percentage
    const percentageProps = useMemo(
        () => ({
            value: slice.primary?.progress_bar_percent
                ? parseInt(slice.primary?.progress_bar_percent)
                : 0,
            description: slice.primary?.progress_quote ? (
                <PrismicRichText field={slice.primary.progress_quote} />
            ) : (
                ''
            ),
        }),
        [slice],
    );

    // Boxes
    const doctorFactsBox1Props = useMemo(
        () => ({
            title: slice.primary?.box1_name || '',
            description: slice.primary?.box1_description ? (
                <PrismicRichText field={slice.primary.box1_description} />
            ) : (
                ''
            ),
        }),
        [slice],
    );
    const doctorFactsBox2Props = useMemo(
        () => ({
            title: slice.primary.box2_name || '',
            description: slice.primary?.box2_description ? (
                <PrismicRichText field={slice.primary.box2_description} />
            ) : (
                ''
            ),
        }),
        [slice],
    );
    const doctorFactsBox3Props = useMemo(
        () => ({
            title: slice.primary.box3_name || '',
            percentageBox: [
                {
                    value: slice.primary?.box3_item1_value
                        ? parseInt(slice.primary?.box3_item1_value)
                        : 0,
                    description: slice.primary?.box3_item1_description ? (
                        <PrismicRichText
                            field={slice.primary.box3_item1_description}
                        />
                    ) : (
                        ''
                    ),
                },
                {
                    value: slice.primary?.box3_item2_value
                        ? parseInt(slice.primary?.box3_item2_value)
                        : 0,
                    description: slice.primary?.box3_item2_description ? (
                        <PrismicRichText
                            field={slice.primary.box3_item2_description}
                        />
                    ) : (
                        ''
                    ),
                },
            ],
            disabled: !slice.primary?.box3_enabled,
        }),
        [slice],
    );
    const doctorFactsBox4Props = useMemo(
        () => ({
            title: slice.primary.box4_name || '',
            description: slice.primary?.box4_description ? (
                <PrismicRichText field={slice.primary.box4_description} />
            ) : (
                ''
            ),
        }),
        [slice],
    );

    // Call to action
    const ctaBoxProps = useMemo(
        () => ({
            text: slice.primary?.call_to_action_text ? (
                <PrismicRichText field={slice.primary.call_to_action_text} />
            ) : (
                ''
            ),
            button: {
                url:
                    (
                        slice.primary
                            ?.call_to_action_button_url as FilledLinkToWebField
                    ).url || '',
                text: slice.primary?.call_to_action_button_text || '',
                style: 'green',
            },
        }),
        [slice],
    );

    // References
    const referencesProps = useMemo(
        () =>
            slice?.items?.map((item) => ({
                text: item?.reference_text || '',
                url: (item?.reference_url as FilledLinkToWebField).url || '',
                target:
                    (item?.reference_url as FilledLinkToWebField).target ||
                    undefined,
            })),
        [slice],
    );

    return (
        <section className="bg-sand-light py-8 md:py-16">
            <Suspense fallback={<Loading />}>
                <DoctorFacts
                    title={slice.primary?.title || ''}
                    doctor={doctorFactsCardProps}
                    percentage={percentageProps}
                    box1={doctorFactsBox1Props}
                    box2={doctorFactsBox2Props}
                    box3={doctorFactsBox3Props}
                    box4={doctorFactsBox4Props}
                    ctaBox={ctaBoxProps}
                    references={referencesProps}
                />
            </Suspense>
        </section>
    );
};

export default Facts;
